import { useState, useEffect } from 'react';
import './design-system/tokens.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryProvider } from './providers/QueryProvider';
import { AppProvider, useApp } from './contexts/AppContext';
import { ToastProvider } from './contexts/ToastContext';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './views/Login';
import ForgotPassword from './views/ForgotPassword';
import ResetPassword from './views/ResetPassword';
import CommandCenter from './views/CommandCenter';
import OrgChart from './views/OrgChart';
import DepartmentView from './views/DepartmentView';
import AgentView from './views/AgentView';
import ExceptionTracker from './views/ExceptionTracker';
import Operations from './views/Operations';
import FinancialReports from './views/FinancialReports';
import Analytics from './views/Analytics';
import AIGovernance from './views/AIGovernance';
import SkillWorkflows from './views/SkillWorkflows';
import PlatformIntelligence from './views/PlatformIntelligence';
import KnowledgeLibrary from './views/KnowledgeLibrary';
import DecisionEngine from './views/DecisionEngine';
import DataManagement from './views/DataManagement';
import AuditCompliance from './views/AuditCompliance';
import AppConfiguration from './views/AppConfiguration';
import CfoDashboard from './views/CfoDashboard';
import CfoBoardView from './views/CfoBoardView';
import FinancialWorkspace from './views/FinancialWorkspace';
import AgentTasks from './views/AgentTasks';
import SSOCallback from './views/SSOCallback';
import VendorManagement from './views/VendorManagement';
import PurchaseOrders from './views/PurchaseOrders';
import GoodsReceipts from './views/GoodsReceipts';
import APInvoices from './views/APInvoices';
// Legacy P2P PaymentRuns removed 2026-05-15 -- consolidated into Treasury -> Payment Runs.
import SalesOrders from './views/SalesOrders';
import CustomerMaster from './views/CustomerMaster';
import ARInvoicesView from './views/ARInvoices';
import RevenueRecognition from './views/RevenueRecognition';
import CashApplication from './views/CashApplication';
import CollectionsView from './views/Collections';
import CreditRisk from './views/CreditRisk';
import ContractManagement from './views/ContractManagement';
import ContractIntelligence from './views/ContractIntelligence';
import RevenueWaterfall from './views/RevenueWaterfall';
import RevenueReconciliation from './views/RevenueReconciliation';
import RevenueReviews from './views/RevenueReviews';
import CashPosition from './views/CashPosition';
import CashForecast from './views/CashForecast';
import FXManagement from './views/FXManagement';
import ScheduledJobs from './views/ScheduledJobs';
import DebtManagement from './views/DebtManagement';
import EquityManagement from './views/EquityManagement';
import CloseManagementView from './views/CloseManagement';
import InsuranceManagement from './views/InsuranceManagement';
import ExpenseManagement from './views/ExpenseManagement';
import Procurement from './views/Procurement';
import BankReconciliation from './views/BankReconciliation';
import ReimbursementPaymentRuns from './views/ReimbursementPaymentRuns';
import ICNetting from './views/ICNetting';
import ContractDetail from './views/ContractDetail';
import ContractRenewals from './views/ContractRenewals';
import RevenueContracts from './views/RevenueContracts';
import RevenueModifications from './views/RevenueModifications';
import RevenueSchedule from './views/RevenueSchedule';
import Step3Tracker from './views/Step3Tracker';
import SSPLibrary from './views/SSPLibrary';
import ProgressTracker from './views/ProgressTracker';
import RevenueDisclosures from './views/RevenueDisclosures';
import SOX404Report from './views/SOX404Report';
import CECLPools from './views/CECLPools';
import CECLDisclosure from './views/CECLDisclosure';
import CECLAdminConfig from './views/CECLAdminConfig';
import DataQualityWarnings from './views/DataQualityWarnings';
import InventoryView from './views/Inventory';
import HRPeople from './views/HRPeople';
import EmployeeCard from './views/EmployeeCard';
import Legal from './views/Legal';
import Commercial from './views/Commercial';
import SupplyChain from './views/SupplyChain';
import FixedAssets from './views/FixedAssets';
import ICTransactions from './views/ICTransactions';
import GLWorkbench from './views/GLWorkbench';
import BudgetManagement from './views/BudgetManagement';
import PlanningStudio from './views/PlanningStudio';
import LeaseAccounting from './views/LeaseAccounting';
import BankAccounts from './views/BankAccounts';
import ProjectCosting from './views/ProjectCosting';
import Integrations from './views/Integrations';
import TaxManagement from './views/TaxManagement';
import UserManagement from './views/UserManagement';
import OnboardingWizard from './views/OnboardingWizard';
import TenantManagement from './views/TenantManagement';
import DOAMatrix from './views/DOAMatrix';
import ProfitabilityAnalysis from './views/ProfitabilityAnalysis';
import ErrorBoundary from './components/ErrorBoundary';
import { CommandPalette } from './components/CommandPalette';
import { ShortcutsHelp } from './components/ShortcutsHelp';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import './App.css';

function ErrorBanner() {
  const { apiError, refreshData } = useApp();
  if (!apiError) return null;
  return (
    <div style={{
      padding: '10px 20px', background: '#dc2626', color: '#fff',
      fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center',
      gap: 12, position: 'sticky', top: 0, zIndex: 100,
    }}>
      <span style={{ flex: 1 }}>{apiError}</span>
      <button onClick={refreshData} style={{
        padding: '4px 12px', background: 'rgba(255,255,255,0.2)',
        border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6,
        color: '#fff', fontSize: 12, cursor: 'pointer',
      }}>Retry</button>
    </div>
  );
}

function ExceptionRedirect() {
  const { exceptionId } = useParams<{ exceptionId: string }>();
  return <Navigate to={`/activity-tracker/${exceptionId}`} replace />;
}

function AuthenticatedApp() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    isCommandPaletteOpen, setCommandPaletteOpen,
    isShortcutsHelpOpen, setShortcutsHelpOpen,
  } = useKeyboardNav();

  // Close mobile menu on navigation
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <div className="shell">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-navy-900 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium">
        Skip to main content
      </a>
      <Sidebar mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <div className="right-col">
        <TopBar onHamburgerClick={() => setMobileMenuOpen(prev => !prev)} />
        <ErrorBanner />
        <CommandPalette open={isCommandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
        <ShortcutsHelp open={isShortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
        <main id="main-content" className="main">
          <ErrorBoundary>
          <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
          <Routes>
            {/* Main pages — one per sidebar item */}
            <Route path="/" element={<OrgChart />} />
            <Route path="/financial-workspace" element={<FinancialWorkspace />} />
            <Route path="/agent-tasks" element={<AgentTasks />} />
            <Route path="/cfo-dashboard" element={<CfoDashboard />} />
            <Route path="/cfo-board" element={<CfoBoardView />} />
            <Route path="/department/:department" element={<DepartmentView />} />
            <Route path="/department/:department/:agentId" element={<AgentView />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/activity-tracker" element={<ExceptionTracker />} />
            <Route path="/activity-tracker/:exceptionId" element={<ExceptionTracker />} />
            <Route path="/financial-reports" element={<FinancialReports />} />
            <Route path="/decision-engine" element={<DecisionEngine />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/workflows" element={<SkillWorkflows />} />
            <Route path="/governance" element={<AIGovernance />} />
            <Route path="/knowledge-library" element={<KnowledgeLibrary />} />
            <Route path="/intelligence" element={<PlatformIntelligence />} />
            <Route path="/data-management" element={<DataManagement />} />
            <Route path="/audit-compliance" element={<AuditCompliance />} />
            <Route path="/configuration" element={<AppConfiguration />} />

            {/* Procure-to-Pay (Module 1) */}
            <Route path="/vendors" element={<VendorManagement />} />
            <Route path="/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/goods-receipts" element={<GoodsReceipts />} />
            <Route path="/ap-invoices" element={<APInvoices />} />
            {/* Unified Payment Runs (vendor + reimbursement) */}
            <Route path="/payment-runs" element={<ReimbursementPaymentRuns />} />

            {/* Order-to-Cash (Module 2) */}
            <Route path="/customers" element={<CustomerMaster />} />
            <Route path="/sales-orders" element={<SalesOrders />} />
            <Route path="/ar-invoices" element={<ARInvoicesView />} />
            <Route path="/revenue-recognition" element={<RevenueRecognition />} />
            <Route path="/cash-application" element={<CashApplication />} />
            <Route path="/collections" element={<CollectionsView />} />
            <Route path="/credit-risk" element={<CreditRisk />} />

            {/* Contract Management (Module 3) + Revenue (Module 4) */}
            <Route path="/contracts" element={<ContractManagement />} />
            <Route path="/contracts/intelligence" element={<ContractIntelligence />} />
            <Route path="/contracts/:id" element={<ContractDetail />} />
            <Route path="/contract-renewals" element={<ContractRenewals />} />
            <Route path="/revenue-waterfall" element={<RevenueWaterfall />} />
            <Route path="/revenue-reconciliation" element={<RevenueReconciliation />} />
            <Route path="/revenue-reviews" element={<RevenueReviews />} />
            <Route path="/revenue-contracts" element={<RevenueContracts />} />
            <Route path="/revenue-modifications" element={<RevenueModifications />} />
            <Route path="/revenue-schedule" element={<RevenueSchedule />} />
            <Route path="/step3-tracker" element={<Step3Tracker />} />
            <Route path="/ssp-library" element={<SSPLibrary />} />
            <Route path="/progress-tracker" element={<ProgressTracker />} />
            <Route path="/revenue-disclosures" element={<RevenueDisclosures />} />
            <Route path="/sox404-report" element={<SOX404Report />} />
            <Route path="/cecl-pools" element={<CECLPools />} />
            <Route path="/cecl-disclosure" element={<CECLDisclosure />} />
            <Route path="/cecl-admin" element={<CECLAdminConfig />} />

            {/* Treasury (Module 5) */}
            <Route path="/treasury" element={<CashPosition />} />
            <Route path="/cash-forecast" element={<CashForecast />} />
            <Route path="/fx-management" element={<FXManagement />} />
            <Route path="/bank-reconciliation" element={<BankReconciliation />} />
            {/* Backward-compat redirect for old reimbursement-only URL */}
            <Route path="/reimbursement-runs" element={<Navigate to="/payment-runs" replace />} />
            <Route path="/ic-netting" element={<ICNetting />} />
            <Route path="/ic-transactions" element={<ICTransactions />} />
            <Route path="/covenants" element={<Navigate to="/debt" replace />} />
            <Route path="/admin/scheduled-jobs" element={<ScheduledJobs />} />
            <Route path="/debt" element={<DebtManagement />} />
            <Route path="/equity" element={<EquityManagement />} />
            <Route path="/close-management" element={<CloseManagementView />} />
            <Route path="/insurance" element={<InsuranceManagement />} />

            {/* Data Quality */}
            <Route path="/data-quality" element={<DataQualityWarnings />} />

            {/* Expense Management (Module 6) */}
            <Route path="/expenses" element={<ExpenseManagement />} />

            {/* Procurement (Module 7) */}
            <Route path="/procurement" element={<Procurement />} />

            {/* Inventory (Module 8) */}
            <Route path="/inventory" element={<InventoryView />} />

            {/* Fixed Assets (Module 13) */}
            <Route path="/fixed-assets" element={<FixedAssets />} />

            {/* HR / People (Module 9) */}
            <Route path="/hr" element={<HRPeople />} />
            <Route path="/hr/employees/:id" element={<EmployeeCard />} />

            {/* Legal (Module 10) */}
            <Route path="/legal" element={<Legal />} />

            {/* Commercial (Module 11) */}
            <Route path="/commercial" element={<Commercial />} />

            {/* Supply Chain (Module 12) */}
            <Route path="/supply-chain" element={<SupplyChain />} />

            {/* Compliance (Module 14) */}
            <Route path="/tax" element={<TaxManagement />} />

            {/* General Ledger */}
            <Route path="/gl-workbench" element={<GLWorkbench />} />
            <Route path="/budget" element={<BudgetManagement />} />
            <Route path="/planning" element={<PlanningStudio />} />
            <Route path="/lease-accounting" element={<LeaseAccounting />} />
            <Route path="/bank-accounts" element={<BankAccounts />} />
            <Route path="/project-costing" element={<ProjectCosting />} />
            <Route path="/profitability" element={<ProfitabilityAnalysis />} />
            <Route path="/integrations" element={<Integrations />} />

            {/* Administration */}
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/tenants" element={<TenantManagement />} />
            <Route path="/doa-matrix" element={<DOAMatrix />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />

            {/* Backward compatibility redirects */}
            <Route path="/command-center" element={<Navigate to="/" />} />
            <Route path="/command-center/:department" element={<Navigate to="/" />} />
            <Route path="/control-center" element={<Navigate to="/operations" />} />
            <Route path="/insights" element={<Navigate to="/analytics" />} />
            <Route path="/exception-tracker" element={<Navigate to="/activity-tracker" />} />
            <Route path="/exception-tracker/:exceptionId" element={<ExceptionRedirect />} />
            <Route path="/task-center" element={<Navigate to="/activity-tracker" />} />
            <Route path="/tasks" element={<Navigate to="/activity-tracker" />} />
            <Route path="/audit-log" element={<Navigate to="/activity-tracker" />} />
            <Route path="/audit" element={<Navigate to="/activity-tracker" />} />
            <Route path="/ai-governance" element={<Navigate to="/governance" />} />
            <Route path="/workflow-tests" element={<Navigate to="/workflows" />} />
            {/* Old direct routes → new tabbed pages */}
            <Route path="/inbox" element={<Navigate to="/operations" />} />
            <Route path="/approval-queue" element={<Navigate to="/operations" />} />
            <Route path="/provisional-review" element={<Navigate to="/operations" />} />
            <Route path="/notifications" element={<Navigate to="/operations" />} />
            <Route path="/upload" element={<Navigate to="/data-management" />} />
            <Route path="/sftp" element={<Navigate to="/data-management" />} />
            <Route path="/fx-rates" element={<Navigate to="/data-management" />} />
            <Route path="/coa-mapping" element={<Navigate to="/data-management" />} />
            <Route path="/compliance" element={<Navigate to="/audit-compliance" />} />
            <Route path="/ia-report" element={<Navigate to="/audit-compliance" />} />
            <Route path="/subledger" element={<Navigate to="/audit-compliance" />} />
            <Route path="/task-queue" element={<Navigate to="/audit-compliance" />} />
            <Route path="/thresholds" element={<Navigate to="/configuration" />} />
            <Route path="/detection-rules" element={<Navigate to="/configuration" />} />
            <Route path="/reference-library" element={<Navigate to="/configuration" />} />
            <Route path="/sso-settings" element={<Navigate to="/configuration" />} />
            <Route path="/trends" element={<Navigate to="/analytics" />} />
            <Route path="/correlations" element={<Navigate to="/analytics" />} />
            <Route path="/decisions" element={<Navigate to="/analytics" />} />
            <Route path="/ai-quality" element={<Navigate to="/analytics" />} />
            {/* Old financial report routes */}
            <Route path="/reports/trial-balance" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/balance-sheet" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/income-statement" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/cash-flow" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/consolidated" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/conversion" element={<Navigate to="/financial-reports" />} />
            <Route path="/reports/posted-jes" element={<Navigate to="/financial-reports" />} />
            <Route path="/trial-balance" element={<Navigate to="/financial-reports" />} />
            <Route path="/balance-sheet" element={<Navigate to="/financial-reports" />} />
            <Route path="/income-statement" element={<Navigate to="/financial-reports" />} />
            <Route path="/cash-flow" element={<Navigate to="/financial-reports" />} />
            <Route path="/conversion" element={<Navigate to="/financial-reports" />} />
            <Route path="/posted-jes" element={<Navigate to="/financial-reports" />} />
          </Routes>
          </motion.div>
          </AnimatePresence>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <QueryProvider>
    <BrowserRouter>
      <ToastProvider>
      <AppProvider>
        <Routes>
          {/* Public routes — no shell layout */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sso/callback" element={<SSOCallback />} />
          {/* Protected routes — shell layout with auth gate */}
          <Route path="/*" element={
            <ProtectedRoute>
              <AuthenticatedApp />
            </ProtectedRoute>
          } />
        </Routes>
      </AppProvider>
      </ToastProvider>
    </BrowserRouter>
    </QueryProvider>
  );
}
